import { BankAccountDto } from "api/bank-account";
import BadgeTooltip from "components/ui/BadgeTooltip";
import { Pencil, Trash2 } from "lucide-react";

interface BankAccountProps {
  bankAccount: BankAccountDto;
  isSelected: boolean;
  onSelect: (bankAccount: BankAccountDto) => void;
  onDelete?: (bankAccountId: string) => void;
  onEdit?: (bankAccount: BankAccountDto) => void;
}

export const BankAccount: React.FC<BankAccountProps> = ({
  bankAccount,
  isSelected,
  onSelect,
  onDelete,
  onEdit,
}) => {
  // Raccourcir l'IBAN : FR76 •••• 3833
  const formatIbanShort = (iban: string) => {
    const clean = iban.replace(/\s/g, '')
    if (clean.length <= 8) return clean
    return `${clean.slice(0, 4)} •••• ${clean.slice(-4)}`
  }

  return (
    <div
      className={`group flex items-center gap-3 px-4 py-3 rounded-md w-full cursor-pointer transition-all duration-200 ${
        isSelected
          ? 'bg-gray-50 border border-primary'
          : 'bg-white border border-gray-300 hover:border-primary'
      }`}
      onClick={() => onSelect(bankAccount)}
    >
      {/* Radio button */}
      <span className={`
        flex-shrink-0 w-4 h-4 rounded-full border flex items-center justify-center transition-colors group-hover:border-primary
        ${isSelected ? 'border-primary bg-white' : 'border-gray-400 bg-white'}
      `}>
        {isSelected && (
          <span className="w-2 h-2 rounded-full bg-primary" />
        )}
      </span>

      {/* Content */}
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <BadgeTooltip content={bankAccount.iban.replace(/(.{4})/g, '$1 ').trim()} position="top">
          <span className={`text-sm font-mono flex-shrink-0 ${isSelected ? 'font-bold text-primary' : 'font-semibold text-gray-800'}`}>
            {formatIbanShort(bankAccount.iban)}
          </span>
        </BadgeTooltip>
        {bankAccount.label && (
          <span className="text-sm text-gray-400 truncate">{bankAccount.label}</span>
        )}
      </div>

      {/* Edit */}
      {onEdit && (
        <button
          className="flex-shrink-0 p-1.5 rounded-lg transition-all text-gray-300 hover:bg-blue-50 hover:text-blue-500 cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(bankAccount);
          }}
        >
          <Pencil className="w-4 h-4" />
        </button>
      )}

      {/* Delete */}
      {onDelete && (
        <button
          className={`flex-shrink-0 p-1.5 rounded-lg transition-all ${
            isSelected
              ? 'text-gray-200 cursor-not-allowed'
              : 'text-gray-300 hover:bg-red-50 hover:text-red-500 cursor-pointer'
          }`}
          disabled={isSelected}
          onClick={(e) => {
            e.stopPropagation();
            onDelete(bankAccount.id);
          }}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};
